// The width of the table region the reader can see, so state content centers in it while the table
// scrolls sideways underneath.

import { useEffect, useState } from 'react'

const REGION = '.me-table-region'

/**
 * The region's horizontal padding. The narrow layout pads the region for focus rings, and a sticky
 * surface pins at that padding edge, so a surface as wide as the client width would run past the region's
 * right edge by the padding.
 * @param {HTMLElement} region
 * @returns {number} pixels
 */
export const regionPadding = region => {
  const style = window.getComputedStyle(region)
  return (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
}

/**
 * @param {{current: ?HTMLElement}} ref an element inside the table region
 * @param {?number} provided the region's client width, when the geometry module already measured it
 * @returns {?number} the region's content width in pixels, or null when unknown (the surface then spans
 *   its cell)
 */
export const useVisibleWidth = (ref, provided) => {
  const [measured, setMeasured] = useState(null)
  const [padding, setPadding] = useState(0)
  const useProvided = typeof provided === 'number' && provided > 0

  useEffect(() => {
    if (!ref.current) return undefined
    const region = ref.current.closest(REGION)
    if (!region) return undefined
    const read = () => {
      setPadding(regionPadding(region))
      if (!useProvided) setMeasured(region.clientWidth || null)
    }
    read()
    if (typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', read)
      return () => window.removeEventListener('resize', read)
    }
    const observer = new window.ResizeObserver(read)
    observer.observe(region)
    return () => observer.disconnect()
  }, [ref, useProvided])

  const width = useProvided ? provided : measured
  return width ? Math.max(0, width - padding) : width
}
